<template>
  <div class="fmt-tool">
    <div class="fmt-toolbar">
      <span class="fmt-label">{{ $t('tool.CodeEditor.title') }}</span>
      <el-select v-model="lang" size="small" style="width:120px" @change="onLangChange">
        <el-option v-for="o in langs" :key="o.value" :label="o.label" :value="o.value" />
      </el-select>
      <span class="fmt-spacer" />
      <el-button size="small" icon="el-icon-s-operation" @click="format">{{ $t("common.action.format") }}</el-button>
      <el-button size="small" plain icon="el-icon-document-copy" @click="doCopy">{{ $t("common.action.copy") }}</el-button>
      <el-button size="small" plain @click="loadSample">{{ $t("common.action.sample") }}</el-button>
      <el-button size="small" plain icon="el-icon-delete" @click="clearAll">{{ $t("common.action.clear") }}</el-button>
    </div>
    <el-input v-model="code" type="textarea" :rows="18" :placeholder="placeholders[lang]" class="fmt-textarea" />
    <div class="fmt-status-bar">
      <span v-if="errorMsg" class="fmt-err"><i class="el-icon-warning" /> {{ errorMsg }}</span>
      <span v-else-if="code.trim()" class="fmt-ok"><i class="el-icon-circle-check" /> {{ charCount }}</span>
      <span v-else class="fmt-hint">{{ placeholders[lang] }}</span>
    </div>
  </div>
</template>

<script>
var SAMPLES = {
  js: "function hello(){\nconsole.log('Hello');\n}\nhello();",
  html: '<div><p>Hello</p><span>World</span></div>',
  css: 'body{color:red;font-size:14px}h1{margin:0}',
  sql: 'select id,name,created_at from users where status=1 order by id desc limit 10',
  java: 'public class Hello{\npublic static void main(String[] args){\nSystem.out.println("Hello");\n}\n}',
  c: '#include <stdio.h>\nint main(){\nprintf("Hello");\nreturn 0;\n}',
  cpp: '#include <iostream>\nusing namespace std;\nint main(){\ncout<<"Hello";\nreturn 0;\n}',
  python: 'def hello(name):\nprint(f"Hello {name}")\nhello("World")',
  go: 'package main\nimport "fmt"\nfunc main(){\nfmt.Println("Hello")\n}'
}

var LANG_KEY = { js:'phJs', html:'phHtml', css:'phCss', sql:'phSql', java:'phJava', c:'phC', cpp:'phCpp', python:'phPython', go:'phGo' }

function formatSql(sql, compress) {
  if (!sql || !sql.trim()) return ''
  var compounds = ['LEFT JOIN','RIGHT JOIN','INNER JOIN','OUTER JOIN','CROSS JOIN','FULL JOIN','NATURAL JOIN','ORDER BY','GROUP BY','PARTITION BY','UNION ALL','INSERT INTO','DELETE FROM','IS NULL','IS NOT NULL','NOT IN','NOT EXISTS']
  var s = sql.replace(/\s+/g, ' ')
  compounds.forEach(function(c) { s = s.replace(new RegExp(c.replace(/ /g, '\\s+'), 'gi'), function(m) { return m.replace(/\s+/g, '_') }) })

  var tokens = [], re = /'[^']*'|"[^"]*"|`[^`]*`|--[^\n]*|\w+(?:\.\w+)*|[^\s\w]/g, m
  while ((m = re.exec(s)) !== null) tokens.push(m[0])
  if (!tokens.length) return ''

  var kw = new Set(['SELECT','FROM','WHERE','AND','OR','NOT','IN','IS','NULL','TRUE','FALSE','AS','ON','LIKE','BETWEEN','EXISTS','CASE','WHEN','THEN','ELSE','END','JOIN','LEFT','RIGHT','INNER','OUTER','CROSS','FULL','NATURAL','LEFT_JOIN','RIGHT_JOIN','INNER_JOIN','OUTER_JOIN','CROSS_JOIN','FULL_JOIN','NATURAL_JOIN','ORDER','BY','ORDER_BY','GROUP','GROUP_BY','HAVING','LIMIT','OFFSET','INSERT','INTO','INSERT_INTO','VALUES','UPDATE','SET','DELETE','FROM','DELETE_FROM','CREATE','TABLE','ALTER','DROP','UNION','UNION_ALL','ALL','DISTINCT','ASC','DESC','IS_NULL','IS_NOT_NULL','NOT_IN','NOT_EXISTS','OVER','PARTITION','PARTITION_BY','WITH','BEGIN','COMMIT','ROLLBACK'])

  var breaks = {
    SELECT:{lvl:0,next:1,nextLine:true}, FROM:{lvl:0,next:0},
    LEFT_JOIN:{lvl:0,next:0}, RIGHT_JOIN:{lvl:0,next:0}, INNER_JOIN:{lvl:0,next:0},
    OUTER_JOIN:{lvl:0,next:0}, CROSS_JOIN:{lvl:0,next:0}, FULL_JOIN:{lvl:0,next:0}, JOIN:{lvl:0,next:0},
    WHERE:{lvl:0,next:1,nextLine:true}, AND:{lvl:1,next:1}, OR:{lvl:1,next:1},
    ORDER_BY:{lvl:0,next:0}, GROUP_BY:{lvl:0,next:0}, HAVING:{lvl:0,next:0},
    LIMIT:{lvl:0,next:0}, OFFSET:{lvl:0,next:0},
    UNION:{lvl:0,next:0}, UNION_ALL:{lvl:0,next:0},
    WHEN:{lvl:0,next:1,nextLine:true}, ELSE:{lvl:0,next:0}, END:{lvl:0,next:0},
    SET:{lvl:0,next:1,nextLine:true}, VALUES:{lvl:0,next:1,nextLine:true},
    OVER:{lvl:0,next:0}, PARTITION_BY:{lvl:0,next:0}
  }

  if (compress) {
    var kw2 = new Set(['LEFT_JOIN','RIGHT_JOIN','INNER_JOIN','OUTER_JOIN','CROSS_JOIN','FULL_JOIN','NATURAL_JOIN','ORDER_BY','GROUP_BY','PARTITION_BY','UNION_ALL','INSERT_INTO','DELETE_FROM','IS_NULL','IS_NOT_NULL','NOT_IN','NOT_EXISTS'])
    return tokens.map(function(t) { return kw2.has(t.toUpperCase()) ? t.replace(/_/g, ' ') : t }).join(' ')
  }

  var lines = [], INDENT = '  ', line = '', level = 0

  function push() { if (line.trim()) lines.push(line); line = '' }

  tokens.forEach(function(t, i) {
    var upper = t.toUpperCase()
    var isKw = kw.has(upper)
    var display = isKw ? upper.replace(/_/g, ' ') : t

    if (i === 0) {
      if (breaks[upper] !== undefined) {
        var cfg0 = breaks[upper]
        line = INDENT.repeat(cfg0.lvl) + display
        if (cfg0.nextLine) { push(); line = INDENT.repeat(cfg0.next || cfg0.lvl) }
        level = cfg0.next
      } else { line = display }
      return
    }

    if (breaks[upper] !== undefined) {
      var cfg = breaks[upper]
      push()
      line = INDENT.repeat(cfg.lvl) + display
      if (cfg.nextLine) { push(); line = INDENT.repeat(cfg.next || cfg.lvl) }
      level = cfg.next
      return
    }

    if (t === ',') { line += ','; push(); line = INDENT.repeat(level); return }
    if (t === '(') { if (line && line[line.length-1] !== ' ') line += ' '; line += '('; return }
    if (t === ')') { line += ')'; return }
    if (t === ';') { line += ';'; push(); return }

    var ns = line && line[line.length-1] !== ' ' && line[line.length-1] !== '('
    line += (ns ? ' ' : '') + display
  })

  push()
  return lines.join('\n')
}

export default {
  name: 'CodeEditor',
  props: { config: { type: Object, default: () => ({}) } },
  data: () => ({
    code: '', errorMsg: '', lang: 'js',
    langs: [
      { label:'JavaScript', value:'js' }, { label:'HTML', value:'html' },
      { label:'CSS', value:'css' }, { label:'SQL', value:'sql' },
      { label:'Java', value:'java' }, { label:'C', value:'c' },
      { label:'C++', value:'cpp' }, { label:'Python', value:'python' },
      { label:'Go', value:'go' }
    ]
  }),
  computed: {
    charCount() { return this.$t('common.hint.charCount', { n: this.code.length }) },
    placeholders() {
      var t = this
      var map = {}
      Object.keys(LANG_KEY).forEach(function(k) { map[k] = t.$t('tool.CodeEditor.' + LANG_KEY[k]) })
      return map
    }
  },
  methods: {
    onLangChange() { this.errorMsg = '' },
    format() {
      this.errorMsg = ''
      if (!this.code.trim()) return
      switch (this.lang) {
        case 'js': case 'c': case 'cpp': case 'java': case 'go':
          this.code = this.fmtBrace(this.code, this.lang)
          break
        case 'python':
          this.code = this.fmtPython(this.code)
          break
        case 'html': this.code = this.fmtHTML(this.code); break
        case 'css': this.code = this.fmtCSS(this.code); break
        case 'sql': this.code = this.fmtSQL(this.code); break
      }
    },
    /**
     * Brace-language formatter (C / C++ / Java / JS / Go).
     * Protects strings + comments (+ preprocessor lines for C/C++),
     * tokenizes on { } ; only, formats into indented lines, then restores.
     */
    fmtBrace(s, lang) {
      var W = 4
      var ph = Object.create(null), n = 0
      function save(m) { var k = '\x00' + (n++) + '\x00'; ph[k] = m; return k }

      // 1. Protect strings and comments (add trailing \n after line comments)
      var code = s
        .replace(/(["'`])(?:(?!\1|\\).|\\.)*\1/g, save)
        .replace(/\/\/[^\n]*/g, function(m) { return save(m) + '\n' })
        .replace(/\/\*[\s\S]*?\*\//g, save)

      // C/C++ only: protect #preprocessor lines so they stay at column 0
      var isCstyle = lang === 'c' || lang === 'cpp'
      if (isCstyle) {
        code = code.replace(/^[ \t]*#.*$/gm, save)
      }

      // Mark which placeholders are preprocessor lines
      var isPP = Object.create(null)
      if (isCstyle) {
        for (var k in ph) {
          var raw = ph[k]
          if (raw.charAt(0) === '#' || (raw.charAt(0) === '\t' && raw.trim().charAt(0) === '#')) {
            isPP[k] = true
          }
        }
      }

      // Preserve original newlines as sentinel tokens
      var NL = '\x01NL\x01'
      code = code.replace(/\n/g, ' ' + NL + ' ')

      // 2. Tokenize — only split on { } ; (not on ( ) [ ])
      var tokens = code.replace(/([{};])/g, ' $1 ').split(/\s+/).filter(Boolean)

      // 3. Format
      var indent = 0, lines = [], cur = ''

      function emit() {
        var clean = cur.replace(/\s+$/, '')
        if (clean) lines.push(clean)
        cur = ''
      }

      function parenDepth(str) {
        var open = (str.match(/\(/g) || []).length
        var close = (str.match(/\)/g) || []).length
        return open - close
      }

      for (var i = 0; i < tokens.length; i++) {
        var t = tokens[i], next = tokens[i + 1]

        // Original newline → force new line
        if (t === NL) {
          emit()
          cur = ' '.repeat(indent * W)
          continue
        }

        // Preprocessor placeholder → emit as its own line at column 0
        if (isPP[t]) {
          emit()
          lines.push(t)
          continue
        }

        // Dedent before closing brace
        if (t === '}') indent = Math.max(0, indent - 1)
        // Dedent before else / catch / finally
        if (/^(else|catch|finally)\b/.test(t)) indent = Math.max(0, indent - 1)

        if (t === '{') {
          if (cur) cur += ' '
          cur += '{'
          emit()
          indent++
          cur = ' '.repeat(indent * W)
        } else if (t === '}') {
          emit()
          cur = ' '.repeat(indent * W) + '}'
          if (next === ';') {
            cur += ';'; i++; emit(); cur = ' '.repeat(indent * W)
          } else if (next && /^(else|catch|finally|while)\b/.test(next)) {
            cur += ' '
          } else {
            emit(); cur = ' '.repeat(indent * W)
          }
        } else if (t === ';') {
          cur += ';'
          // Only break line if not inside parens (e.g. for(;;) header)
          if (parenDepth(cur) <= 0) {
            emit()
            cur = ' '.repeat(indent * W)
          }
        } else {
          if (!cur) cur = ' '.repeat(indent * W)
          else if (cur.charAt(cur.length - 1) !== ' ') cur += ' '
          cur += t
        }
      }
      emit()

      // 4. Restore protected regions
      var result = lines.join('\n')
      for (var k2 in ph) {
        result = result.split(k2).join(ph[k2])
      }

      // 5. Clean trailing whitespace and excessive blank lines
      return result.split('\n').map(function(l) { return l.replace(/\s+$/, '') }).join('\n')
        .replace(/\n{3,}/g, '\n\n').trim()
    },
    /* Python: colon → indent, blank line resets */
    fmtPython(s) {
      var lines = s.split('\n'), out = [], indent = 0
      for (var i = 0; i < lines.length; i++) {
        var raw = lines[i], l = raw.trim()
        if (!l) { out.push(''); continue }
        // dedent on else/elif/except/finally
        if (/^(else|elif|except|finally|elif\s|else\s)/.test(l)) indent = Math.max(0, indent - 1)
        out.push(' '.repeat(indent * 4) + l)
        if (/^(if|elif|else|for|while|def|class|try|except|finally|with)\b/.test(l) && l.endsWith(':')) indent++
        if (/^return\b|^pass\b|^break\b|^continue\b/.test(l) && i + 1 < lines.length && lines[i+1].trim() && !/^(else|elif|except|finally)/.test(lines[i+1].trim())) {
          indent = Math.max(0, indent - 1)
        }
      }
      return out.join('\n')
    },
    fmtHTML(s) {
      var indent = 0, out = ''
      var tags = s.replace(/></g, '>\n<').replace(/>/g, '>\n').replace(/<\//g, '\n</').split('\n')
      for (var j = 0; j < tags.length; j++) {
        var line = tags[j].trim()
        if (!line) continue
        if (line.startsWith('</')) indent = Math.max(0, indent - 1)
        out += ' '.repeat(indent * 2) + line + '\n'
        if (line.startsWith('<') && !line.startsWith('</') && !line.endsWith('/>') && !line.includes('<!')) indent++
      }
      return out.trim()
    },
    fmtCSS(s) {
      // Normalize whitespace first: collapse spaces, remove leading/trailing
      var normalized = s.replace(/\s+/g, ' ')
      // Insert newlines around braces and after semicolons
      var r = normalized.replace(/\{/g, ' {\n').replace(/\}/g, '\n}\n').replace(/;/g, ';\n').replace(/\n{2,}/g, '\n')
      var lines = r.split('\n'), indent = 0, out = ''
      for (var k = 0; k < lines.length; k++) {
        var l = lines[k].trim()
        if (!l) continue
        if (l === '}' || l.startsWith('}')) indent = Math.max(0, indent - 1)
        if (l.endsWith('{')) {
          out += ' '.repeat(indent * 2) + l.replace(' {', ' {') + '\n'
          indent++
        } else {
          out += ' '.repeat(indent * 2) + l + '\n'
        }
      }
      return out.trim()
    },
    fmtSQL(s) { return formatSql(s, false) },
    doCopy() {
      if (!this.code.trim()) { this.$message.warning(this.$t("common.status.noContent")); return }
      navigator.clipboard.writeText(this.code).then(() => this.$message.success(this.$t("common.status.copied")))
    },
    loadSample() { this.code = SAMPLES[this.lang]; this.errorMsg = '' },
    clearAll() { this.code = ''; this.errorMsg = '' },
    doAction(a) {
      if (a === 'copy') this.doCopy()
      if (a === 'clear') this.clearAll()
      if (a === 'sample') this.loadSample()
    }
  }
}
</script>

<style scoped>
.fmt-tool { display: flex; flex-direction: column; gap: 12px; }

.fmt-toolbar { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }
.fmt-toolbar >>> .el-button + .el-button { margin-left: 0; }
.fmt-label { font-size: 13px; font-weight: 700; color: #1a1f36; }
.fmt-spacer { flex: 1; }

.fmt-textarea >>> textarea {
  font-family: 'JetBrains Mono','Fira Code','Consolas',monospace !important;
  font-size: 13px !important; line-height: 1.6 !important;
  border-radius: 10px; resize: vertical; min-height: 360px;
}

.fmt-status-bar { font-size: 12px; }
.fmt-err { color: #f56c6c; display: flex; align-items: center; gap: 4px; }
.fmt-ok { color: #67c23a; display: flex; align-items: center; gap: 4px; }
.fmt-hint { color: #c0c4cc; }
</style>
